/**
 * Upload queue shared by the page and the service worker (SPEC §13.1): pending uploads
 * live in IndexedDB and retry on reconnect. Blobs are kept locally so the attachment
 * shows immediately (and offline) from a local preview.
 */
import { clear, createStore, del, entries, get, set } from 'idb-keyval'

export interface UploadTask {
  key: string // R2 key, e.g. att/<itemId>/<attId>.webp
  itemId: string
  attId: string
  variant: 'full' | 'thumb'
  mime: string
  tries: number
  createdAt: number
}

/** An upload the server refused for good (too big, wrong type); the page reports and cleans it up. */
export interface UploadFailure {
  key: string
  itemId: string
  attId: string
  variant: 'full' | 'thumb'
  status: number
  at: number
}

const tasks = createStore('terrazzo-uploads', 'tasks')
const blobs = createStore('terrazzo-blobs', 'blobs')
const failures = createStore('terrazzo-upload-failures', 'failures')

/** Signing out: this device keeps none of this person's files (local copies, pending uploads, failures). */
export async function wipeUploads(): Promise<void> {
  await Promise.all([clear(tasks), clear(blobs), clear(failures)])
}

/** Local copies are a cache: past this, the oldest already-uploaded ones are dropped. */
const BLOB_CAP_BYTES = 150 * 1024 * 1024

/**
 * Files are stored as bytes + type rather than Blobs: Safari's private mode (and some
 * WebViews) refuse Blobs in IndexedDB, which made every attachment fail there. Older
 * entries written as Blobs still read fine.
 */
type Stored = Blob | { buf: ArrayBuffer; type: string }
const toBlob = (v: Stored | undefined): Blob | undefined => (!v ? undefined : v instanceof Blob ? v : new Blob([v.buf], { type: v.type }))
const sizeOf = (v: Stored): number => (v instanceof Blob ? v.size : v.buf.byteLength)

export async function enqueue(task: Omit<UploadTask, 'tries' | 'createdAt'>, blob: Blob): Promise<void> {
  await set(task.key, { buf: await blob.arrayBuffer(), type: blob.type || task.mime } satisfies Stored, blobs)
  await set(task.key, { ...task, tries: 0, createdAt: Date.now() } satisfies UploadTask, tasks)
}

export async function localBlob(key: string): Promise<Blob | undefined> {
  return toBlob(await get<Stored>(key, blobs))
}

export async function pendingCount(): Promise<number> {
  return (await entries(tasks)).length
}

let running: Promise<number> | null = null

/** Try every pending upload once. Returns how many remain. */
export function processQueue(fetchImpl: typeof fetch = fetch): Promise<number> {
  if (running) return running
  running = (async () => {
    const list = (await entries<string, UploadTask>(tasks)).map(([, t]) => t).sort((a, b) => a.createdAt - b.createdAt)
    let remaining = 0
    for (const t of list) {
      const blob = toBlob(await get<Stored>(t.key, blobs))
      if (!blob) {
        await del(t.key, tasks)
        continue
      }
      try {
        const res = await fetchImpl(`/api/files/${encodeURIComponent(t.itemId)}/${encodeURIComponent(t.attId)}?variant=${t.variant}`, {
          method: 'PUT',
          headers: { 'content-type': t.mime },
          body: blob,
          credentials: 'same-origin',
        })
        if (res.ok) {
          await del(t.key, tasks)
          // Keep thumbnails locally for offline viewing; drop big originals after upload.
          if (t.variant === 'full' && blob.size > 600_000) await del(t.key, blobs)
          continue
        }
        if (res.status >= 400 && res.status < 500 && res.status !== 401 && res.status !== 408 && res.status !== 429) {
          // Permanent failure (bad type/size): give up on this file, but say so.
          await set(t.key, { key: t.key, itemId: t.itemId, attId: t.attId, variant: t.variant, status: res.status, at: Date.now() } satisfies UploadFailure, failures)
          await del(t.key, tasks)
          await del(t.key, blobs)
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('terrazzo:upload-failed'))
          continue
        }
        await set(t.key, { ...t, tries: t.tries + 1 }, tasks)
        remaining++
      } catch {
        await set(t.key, { ...t, tries: t.tries + 1 }, tasks)
        remaining++
      }
    }
    return remaining
  })().finally(() => {
    running = null
  })
  return running
}

/** Failures recorded since the last call (by the page or the service worker); clears them. */
export async function takeFailures(): Promise<UploadFailure[]> {
  const list = (await entries<string, UploadFailure>(failures)).map(([, f]) => f)
  for (const f of list) await del(f.key, failures)
  return list
}

/** Drop queued uploads and local copies for attachments that no longer exist. */
export async function forget(keys: string[]): Promise<void> {
  for (const k of keys) {
    await del(k, tasks)
    await del(k, blobs)
  }
}

/**
 * Keep the local blob cache bounded: remove copies of attachments that are gone (`live`
 * = every attachment key/thumbKey in the doc), then the oldest uploaded ones over the cap.
 * Pending uploads are never touched.
 */
export async function pruneBlobs(live: Set<string>): Promise<void> {
  const pending = new Set((await entries<string, UploadTask>(tasks)).map(([k]) => k))
  const all = await entries<string, Stored>(blobs)
  let total = 0
  const kept: Array<[string, number]> = []
  for (const [k, b] of all) {
    if (pending.has(k)) continue
    if (!live.has(k)) {
      await del(k, blobs)
      continue
    }
    total += sizeOf(b)
    kept.push([k, sizeOf(b)])
  }
  // Keys end in a time-sortable attachment id, so sorting by key ≈ oldest first.
  kept.sort((a, b) => (a[0].split('/').pop()! < b[0].split('/').pop()! ? -1 : 1))
  for (const [k, size] of kept) {
    if (total <= BLOB_CAP_BYTES) break
    await del(k, blobs)
    total -= size
  }
}
